import { Component, type ErrorInfo, type ReactNode } from "react";

type Props = { children: ReactNode };
type State = { erreur: Error | null };

/**
 * Frontière d'erreur générique.
 *
 * Sans elle, une seule exception pendant le rendu (un champ inattendu à null,
 * un tableau absent) démonte tout l'arbre React : le commercial se retrouve
 * devant une page blanche, sans message, souvent en pleine préparation
 * d'appel. Un écran d'erreur explicite avec un moyen de repartir vaut
 * infiniment mieux qu'un écran vide.
 *
 * Ne rattrape que les erreurs de rendu — pas celles des gestionnaires
 * d'événements ni des requêtes, déjà gérées par react-query.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { erreur: null };

  static getDerivedStateFromError(erreur: Error): State {
    return { erreur };
  }

  componentDidCatch(erreur: Error, infos: ErrorInfo) {
    // la console reste la seule remontée disponible côté navigateur ;
    // la pile de composants est ce qui permet de localiser la panne.
    console.error("Erreur de rendu non rattrapée", erreur, infos.componentStack);
  }

  private reessayer = () => this.setState({ erreur: null });

  render() {
    const { erreur } = this.state;
    if (!erreur) return this.props.children;

    return (
      <div className="panel" style={{ margin: 20 }} role="alert">
        <p><strong>Une erreur est survenue lors de l'affichage.</strong></p>
        <p className="mut">
          La page n'a pas pu s'afficher correctement. Réessayer, ou rafraîchir la page
          si le problème persiste.
        </p>
        <p className="mono petit mut">{erreur.message}</p>
        <div className="ligne">
          <button className="btn" onClick={this.reessayer}>Réessayer</button>
          <button className="btn ghost sm" onClick={() => window.location.reload()}>
            Rafraîchir la page
          </button>
        </div>
      </div>
    );
  }
}
